import React, { ReactElement, useEffect, useState } from 'react';
import { useMasterAccountId } from '../../../hooks/redux';
import addAlert from 'ps-components/AlertToast/addAlert';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import AdvancedRow from 'ps-components/AdvancedTable/AdvancedRow';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import Button from 'ps-components/Button/Button';
import { InputNumber } from 'ps-design';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import { SLIDEOUTS as MarketPaySlideouts } from '../../../components/Slideout/Slideouts';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import { COLUMN_HEADERS, ELEMENT_NAMES, INITIAL_SURVEY_JOB_STATE, MARKET_ELEMENTS, NON_FORMATTED_COLUMNS, NUMERIC_COLUMN_TYPES, STATIC_JOB_INPUTS } from './constants';
import { formatPctValue, renderInput, renderNumberInput, renderSelect, renderTextArea, validateInput } from './helpers';
import { addMatch, getSurveySourceOptions, getUDFFields, savePrivateSurveyJob } from './PrivateSurveyJobMatchService';
import { ICurrency } from './types/currency';
import { ISlideout, ISlideoutOptions, SlideoutType } from './types/slideout';
import { IAddMatchParams, ISaveSurveyJobReturn, ISavePrivateSurveyJobsParams, ISurveySourceOption, IUDFField, IUDFFieldReturn } from './types/privateSurveyJob';
import { IOption } from './types/privateSurvey';
import './PrivateSurveyJobMatch.scss';

const PrivateSurveyJobMatch = (props: {
  benchmarkYear: number;
  currencies: ICurrency[];
  currency: string;
  fromIframe: boolean;
  fromJobDetails: boolean;
  hideSlideout: () => void;
  orgJobCurrency: string;
  orgJobCode: string;
  orgJobCodeKey: string;
  orgJobId: number,
  refreshPrivateSurveyJobTabData: () => void;
  refreshJobTabData: () => void;
  showSlideout: (slideoutType: SlideoutType, slideoutOptions: ISlideoutOptions) => void;
  slideout: ISlideout;
  surveyCode: string;
}): ReactElement => {
  const { benchmarkYear, currencies, currency, fromIframe, fromJobDetails, hideSlideout, orgJobCurrency, orgJobCode, orgJobCodeKey, orgJobId, refreshPrivateSurveyJobTabData, refreshJobTabData, showSlideout, slideout, surveyCode } = props;
  const { bonus, flsa, lti, jobCode, jobCurrency, jobDescription, jobScope, jobTitle, maxRange, midRange, minRange, overtime, surveySource } = STATIC_JOB_INPUTS;
  const masterAccountId = useMasterAccountId();
  const [surveyJobFields, setSurveyJobFields] = useState<ISavePrivateSurveyJobsParams>(INITIAL_SURVEY_JOB_STATE);
  const [hasDefaultCurrency, setHasDefaultCurrency] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [rows, setRows] = useState<string[][]>([]);
  const [surveySourceOptions, setSurveySourceOptions] = useState<IOption[]>([]);
  const [surveyUDFs, setSurveyUDFs] = useState<IUDFField[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const isSaveDisabled =
    !surveyJobFields.job_code ||
    !surveyJobFields.pub_survey_jobs_columns.currency ||
    surveyJobFields.pub_survey_jobs_columns.currency === "All Currencies" ||
    !surveyJobFields.job_scope ||
    !surveyJobFields.survey_code ||
    !surveyJobFields.pub_survey_jobs_columns.job_title ||
    isSaving ||
    hasError;


  useEffect(() => {
    setHasError(document.querySelectorAll('.pxl-has-error').length > 0);
  }, [surveyJobFields, surveyUDFs]);

  useEffect(() => {
    if (slideout.open) {
      getDefaultRows();
      getSurveySources();
      getUDFFieldValues();
      setHasDefaultCurrency(!!currency && currency !== "%");
      if(currency) {
        const selectedCurrency = currencies?.find(curr => curr.code === currency)?.name;
        selectedCurrency && setSurveyJobFields({...surveyJobFields, pub_survey_jobs_columns: {
          ...surveyJobFields.pub_survey_jobs_columns,
          [jobCurrency.id]: selectedCurrency
        }});
      }
    }

    if (!slideout.open) {
      clearInputs();
      if (fromIframe && refreshPrivateSurveyJobTabData) refreshPrivateSurveyJobTabData();
      if (fromIframe && refreshJobTabData) refreshJobTabData();
    }
  }, [slideout.open]);

  useEffect(() => {
    if (surveySourceOptions.length > 0 && surveyCode) {
      const newSurvey = surveySourceOptions.find(survey => {
        return survey.code === surveyCode;
      })?.value || "";

      setSurveyJobFields({...surveyJobFields, survey_code: newSurvey })
    }
  }, [surveySourceOptions])

  const clearInputs = () => {
    setSurveyJobFields(INITIAL_SURVEY_JOB_STATE)
    setRows([]);
    setSurveyUDFs([]);
    setSurveySourceOptions([]);
    setIsSaving(false);
    setIsSaved(false);
   }

  const currencyOptions = currencies?.filter(curr => {
    if (hasDefaultCurrency && orgJobCurrency) {
      return curr.code === orgJobCurrency;
    }
    return curr.code !== '%';
  }).map(currency => { return { text: currency.name, value: currency.name }});

  const formatRowValue = (event: React.FocusEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    const [rowName, columnName] = id.split('-');
    const rowIndex = rows.findIndex((row) => row[0] === rowName);
    const columnIndex = COLUMN_HEADERS.indexOf(columnName);
    const updatedRows = [...rows];
    const isPctInput = rowName.includes('%') && !NON_FORMATTED_COLUMNS.includes(columnName);
    const isCountInput = columnName.includes('No.');
    const hasDecimal = value.includes('.');

    if (isPctInput && value.length > 1 && !hasDecimal) {
      updatedRows[rowIndex][columnIndex] = formatPctValue(value);
    } else if (isCountInput && hasDecimal) {
      const formattedCount = value.split('.').join('');
      updatedRows[rowIndex][columnIndex] = formattedCount;
    } else {
      updatedRows[rowIndex][columnIndex] = value;
    }

    setRows(updatedRows);
  }

  const formatUDFValue = (event: React.FocusEvent<HTMLInputElement>) => {
    let { id, value } = event.target;
    const updatedUDFs = [...surveyUDFs];
    const udfIndex = updatedUDFs?.findIndex((udf) => udf?.col_name === id);
    if (updatedUDFs[udfIndex]?.col_type === 'p' && value.length > 1 && !value.includes('.')) {
      updatedUDFs[udfIndex].value = formatPctValue(value)
      setSurveyUDFs(updatedUDFs);
    }

    return;
  }

  const getDefaultRows = () => {
    let defaultRows = Array.from(Array(22), () => new Array(9).fill(null));
    defaultRows.map((row, index) => row[0] = ELEMENT_NAMES[index]);
    setRows(defaultRows)
  }

  const getFormattedSurveyOption = (option: ISurveySourceOption) => {
    return `${option.survey_short_name} (${option.survey_code})`
  }

  const getMarketElementCard = () => {
    return (
      <ActionableHeaderCard
        cardTitle="Survey Market Compensation Elements"
        className="private-survey-job__card"
        isCollapsible={true}
      >
        {renderFormattingNote()}
        <div className="row">
          <div className="col-md-12">
           <AdvancedTable
              columnHeaders={COLUMN_HEADERS}
              getDataRow={getMarketElementRow}
              headerClassName="sticky"
              isBasic={true}
              rowData={rows}
              staticTable={true}
           />
          </div>
        </div>
      </ActionableHeaderCard>
    )
  }

  const getMarketElementRow = (element: any, elementIndex: any) => {
    const cells = element.map((cellData: any, index: any) => {
      return (
        <AdvancedCell>
          {index === 0 ? element[index] : (
            <InputNumber
              className="market-element__input"
              id={`${rows[elementIndex][0]}-${COLUMN_HEADERS[index]}`}
              min={0}
              onBlur={formatRowValue}
              onChange={handleRowValueOnChange}
              value={rows[elementIndex][index]}
            />
          )}
        </AdvancedCell>
      );
    });

    return <AdvancedRow key={`market-element-${elementIndex}`}>{cells}</AdvancedRow>;
  }

  const getOtherElementsCard = () => {
    return (
      <ActionableHeaderCard
        cardTitle="Survey Market Other Elements"
        className="private-survey-job__card"
        isCollapsible={true}
      >
        {renderFormattingNote()}
        <div className="row">
          {renderNumberInput({
            label: flsa.label,
            id: flsa.id,
            onChange: handleJobsColumnsInputUpdate,
            value: surveyJobFields.pub_survey_jobs_columns.flsa_exempt_pct || ""
          })}
          {renderNumberInput({
            label: lti.label,
            id: lti.id,
            onChange: handleJobsColumnsInputUpdate,
            value: surveyJobFields.pub_survey_jobs_columns.lti_eligible_pct || ""
          })}
        </div>
        <div className="row">
          {renderNumberInput({
            label: bonus.label,
            id: bonus.id,
            onChange: handleJobsColumnsInputUpdate,
            value: surveyJobFields.pub_survey_jobs_columns.bonus_eligible_pct || ""
          })}
          {renderNumberInput({
            label: overtime.label,
            id: overtime.id,
            onChange: handleJobsColumnsInputUpdate,
            value: surveyJobFields.pub_survey_jobs_columns.overtime_eligible_pct || ""
          })}
        </div>
        <div className="row">
          {renderNumberInput({
            label: minRange.label,
            id: minRange.id,
            onChange: handleJobsColumnsInputUpdate,
            value: surveyJobFields.pub_survey_jobs_columns.avg_min || ""
          })}
          {renderNumberInput({
            label: midRange.label,
            id: midRange.id,
            onChange: handleJobsColumnsInputUpdate,
            value:  surveyJobFields.pub_survey_jobs_columns.avg_mid || ""
          })}
        </div>
        <div className="row">
          {renderNumberInput({
            label: maxRange.label,
            id: maxRange.id,
            onChange: handleJobsColumnsInputUpdate,
            value: surveyJobFields.pub_survey_jobs_columns.avg_max || ""
          })}
        </div>
      </ActionableHeaderCard>
    );
  }

  const getSurveyDetailsCard = () => {
    return (
      <ActionableHeaderCard
        cardTitle="Survey Job Details"
        className="private-survey-job__card"
        isCollapsible={true}
      >
        <div className="row">
          <div className="col-md-12">
            {renderSelect({
              label: surveySource.label,
              id: surveySource.id,
              options: surveySourceOptions,
              onChange: handleSelectUpdate,
              required: true,
              value: surveyJobFields.survey_code
            })}
          </div>
        </div>
        <div className="row">
          {renderInput({
            label: jobCode.label,
            id: jobCode.id,
            onChange: handleInputUpdate,
            required: true,
            value: surveyJobFields.job_code,
            error: validateInput(surveyJobFields.job_code)
          })}
          {renderInput({
            label: jobTitle.label,
            id: jobTitle.id,
            onChange: handleJobsColumnsInputUpdate,
            required: true,
            value: surveyJobFields.pub_survey_jobs_columns.job_title,
            error: validateInput(surveyJobFields.pub_survey_jobs_columns.job_title)
          })}
        </div>
        <div className="row">
          <div className="col-md-6">
            {renderSelect({
              label: jobCurrency.label,
              id: jobCurrency.id,
              options: currencyOptions,
              onChange: handleJobsColumnsSelectUpdate,
              isDisabled: hasDefaultCurrency,
              required: true,
              value: surveyJobFields.pub_survey_jobs_columns.currency
            })}
          </div>
          {renderInput({
            label: jobScope.label,
            id: jobScope.id,
            onChange: handleInputUpdate,
            required: true,
            value: surveyJobFields.job_scope,
            error: validateInput(surveyJobFields.job_scope)
          })}
        </div>
        <div className="row">
          {renderTextArea({
            label: jobDescription.label,
            id: jobDescription.id,
            onChange: handleInputUpdate,
            rows: 3,
            value: surveyJobFields.job_description,
            error: surveyJobFields.job_description && validateInput(surveyJobFields.job_description)
          })}
        </div>
      </ActionableHeaderCard>
    );
  }

  const getSurveySources = async () => {
    let options = await getSurveySourceOptions(masterAccountId);
    setSurveySourceOptions(options?.data?.map((option: ISurveySourceOption) => {
      const value = getFormattedSurveyOption(option);
      return { text: value, value, code: option.survey_code }
    }));
  };

  const getUDFCard = () => {
    return (
      <ActionableHeaderCard cardTitle="Survey User-Defined Fields" className="private-survey-job__card" isCollapsible={true}>
        {getUDFInputs()}
      </ActionableHeaderCard>
    );
  }

  const getUDFFieldValues = async () => {
    await getUDFFields(masterAccountId).then((resp: IUDFFieldReturn) => {
      const data = resp.data.map((udf) => { return {...udf, value: ''} });
      setSurveyUDFs(data);
    })
  };

  const getUDFInputs = () => {
    return surveyUDFs?.map((udf: IUDFField, index: number) => {
      if (index % 2 === 0) {
        const { col_name, col_title, col_type, value } = udf;
        const secondColumn = surveyUDFs[index + 1];
        const params = {
          label: col_title,
          id: col_name,
          onChange: handleUDFOnChange,
          value
        };

        return (
          <div className="row" key={`udf-row-${index}`}>
            {NUMERIC_COLUMN_TYPES.includes(col_type) ?
              renderNumberInput({...params, onBlur: formatUDFValue}) :
              renderInput({...params, error: (params.value && validateInput(params.value))})
            }
            {index + 1 !== surveyUDFs.length && renderSecondColumn(secondColumn)}
          </div>
        )
      }

      return;
    })
  }

  const handleInputUpdate = (id: string, value: string) => {
    setSurveyJobFields({...surveyJobFields, [id]: value});
  }

  const handleJobsColumnsInputUpdate = (id: string, value: string) => {
    setSurveyJobFields({...surveyJobFields, pub_survey_jobs_columns: {
      ...surveyJobFields.pub_survey_jobs_columns,
      [id]: value
    }})
  }

  const handleJobsColumnsSelectUpdate = (params: { id: string, value: string }) => {
    const { id, value } = params;
    setSurveyJobFields({...surveyJobFields, pub_survey_jobs_columns: {
      ...surveyJobFields.pub_survey_jobs_columns,
      [id]: value
    }})
  }

  const handleRowValueOnChange = (id: string, value: string) => {
    const [rowName, columnName] = id.split('-');
    const rowIndex = rows.findIndex((row) => row[0] === rowName);
    const columnIndex = COLUMN_HEADERS.indexOf(columnName);
    const updatedRows = [...rows];

    updatedRows[rowIndex][columnIndex] = value.replace('-', "");
    setRows(updatedRows);
  }

  const handleSelectUpdate = (params: { id: string, value: string }) => {
    const { id, value } = params;
    setSurveyJobFields({...surveyJobFields, [id]: value})
  }

  const handleUDFOnChange = (id: string, value: string) => {
    const updatedUDFs = [...surveyUDFs];
    const udfIndex = updatedUDFs?.findIndex((udf) => udf?.col_name === id);
    updatedUDFs[udfIndex].value = value;
    setSurveyUDFs(updatedUDFs);
  }

  const onClose = () => {
    hideSlideout();
  }

  const onSave = () => {
    const currency = currencies.find(option => option.name === surveyJobFields.pub_survey_jobs_columns.currency)?.code;
    const surveyCode = surveySourceOptions.find(option => surveyJobFields.survey_code.includes(option.value))?.code;
    let params = {
      ...surveyJobFields,
      survey_code: surveyCode,
      temp_survey_year: benchmarkYear,
      pub_survey_jobs_columns: {
       ...surveyJobFields.pub_survey_jobs_columns,
       currency
      }
    } as ISavePrivateSurveyJobsParams;

    setIsSaving(true);

    surveyUDFs.map(udf => {
      if (udf.value) {
        params = {
          ...params,
          udf_columns: {
            ...params.udf_columns,
            [udf.col_name]: udf.value
          }
        }
      }
    });

    rows.map((row, rowIndex) => {
      row.map((cell, cellIndex) => {
        if (cellIndex === 0) return;
        if (cell) {
          const rowValue = row[0].toLowerCase().replace(/ /g,"_");
          let columnValue = COLUMN_HEADERS[cellIndex].toLowerCase().replace(/ /g,"_");
          const param = (MARKET_ELEMENTS as any)[rowValue][`${rowValue}_${columnValue}`];
          const paramValue = row[cellIndex];
          params = {
            ...params,
            pub_survey_jobs_columns: {...params.pub_survey_jobs_columns, [param]: paramValue }
          }
        }
      })
    });

    savePrivateSurveyJob(masterAccountId, params)
      .then((resp: ISaveSurveyJobReturn) => {
        const cutId = resp.data?.cutId;
        const addMatchParams = {  benchmarkYear, cut_id: cutId, client_job_code: orgJobCode, client_job_code_key: orgJobCodeKey, orgJobId, showSuggestions: false } as IAddMatchParams;

        if (resp?.status === 409) {
          setIsSaving(false);
          setIsSaved(false);
          addAlert(resp?.detail, 'warning', 5000, 'top-center');
          return;
        }

        if (fromIframe && !fromJobDetails) {
          setIsSaving(false);
          setIsSaved(true);
          addAlert('Successfully create new job', 'success', 5000, 'top-center');
        } else {
          addMatch(masterAccountId, addMatchParams).then(() => {
            setIsSaving(false);
            setIsSaved(true);
            addAlert('Successfully added match to pricing', 'success', 5000, 'top-center');
          });
        }
      }).catch(e => {
        setIsSaving(false);
        if (fromIframe && !fromJobDetails) {
          addAlert('Error creating new job', 'danger', 5000, 'top-center');
        } else {
          addAlert('Error adding match to pricing', 'danger', 5000, 'top-center');
        }
      });
  }

  const renderFormattingNote = () => {
    return (
      <div className="row">
        <div className="col-md-12 card-sub-header">
          <b>Note:</b> Enter percentages as decimal amounts (i.e. 25% as 0.25)
        </div>
      </div>
    );
  }

  const renderSecondColumn = (column: IUDFField) => {
    const { col_name, col_title, col_type, value } = column;
    const params = {
      label: col_title,
      id: col_name,
      onChange: handleUDFOnChange,
      value
    };
    return NUMERIC_COLUMN_TYPES.includes(col_type) ?
      renderNumberInput({...params, onBlur: formatUDFValue}) :
      renderInput({...params, error: (params.value && validateInput(params.value))})
  }

  function showAddPrivateSurveySlideout() {
    showSlideout(MarketPaySlideouts.PRIVATE_SURVEY, {
      navigateToPrevSlideout: showAddPrivateSurveyJobSlideout
    });
  }

  function showAddPrivateSurveyJobSlideout(surveyCode: string) {
    showSlideout(MarketPaySlideouts.PRIVATE_SURVEY_JOB_MATCH, {
      benchmarkYear,
      currencies,
      currency,
      orgJobCode,
      orgJobCodeKey,
      orgJobCurrency,
      orgJobId,
      surveyCode
    });
  }

  return (
    <React.Fragment>
      {!isSaved ? (
        <React.Fragment>
          <ProgressStatusOverlay
            inProgress={isSaving}
            primaryText='Saving Job Details...'
            submitSuccessDetails="This might take a moment"
          />
          <div className="private-survey-job__container">
            <SlideoutHeader title="Add Private Survey Job Match" doClose={onClose} />
            {((fromIframe && fromJobDetails) || !fromIframe) && (
              <div className="private-survey-job__sub-header">
                <Button className="private-survey__btn pxl-btn pxl-btn-primary" onClick={showAddPrivateSurveySlideout}>
                  Add a Private Survey
                </Button>
              </div>
            )}
            {getSurveyDetailsCard()}
            {getMarketElementCard()}
            {getOtherElementsCard()}
            {getUDFCard()}
          </div>
          <SlideoutFooter isBoxShadowTheme={true}>
            <SlideoutFooterStandardButtons applyText="Save" doApply={onSave} doCancel={onClose} isApplyDisabled={isSaveDisabled}/>
          </SlideoutFooter>
        </React.Fragment>
      ) : (
        <div className="private-survey-job__container">
          <SlideoutSubmitSuccess
            actionFunction={onClose}
            actionText="Done"
            closeAction={onClose}
            submitSuccessDetails='A new survey job has been created and added to your pricing.'
            submitSuccessTitle='Job details have been saved!'
          />
        </div>
      )}
    </React.Fragment>
  );
}

export default PrivateSurveyJobMatch;
